import React, {useEffect} from "react";
import {observer} from "mobx-react-lite";
import {Alert, CircularProgress, Grid, TextField, Typography} from "@mui/material";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {EditorModalComponent} from "../../../taxonomy-editor/EditorModalComponent";
import {useStores} from "../../../../stores";
import {environment} from "../../../../env";
import {useRollbar} from "@rollbar/react";
import type {CreateCategorizationApprovalStats} from "../../../../services/classes/AiClasses";
import {CurrencyComponent} from "../../../../components/currency-component/CurrencyComponent";

export const ConfirmSendForApproval: React.FC = observer(() => {
    const {categorizationReviewStore, materializedApi} = useStores();
    const rollbar = environment.rollbarConfig ? useRollbar() : console;
    const approvalController = categorizationReviewStore.reviewPageController.reviewPageApprovalController;

    const [isLoading, setIsLoading] = React.useState(false);
    const [approvalStats, setApprovalStats] = React.useState<CreateCategorizationApprovalStats | undefined>(undefined);
    useEffect(() => {
        if (isLoading) return;
        if (approvalStats) return;
        if (!categorizationReviewStore.bagId || categorizationReviewStore.bagId <= 0) {
            console.warn('No bagId, cannot get approval stats')
        }
        setIsLoading(true)
        materializedApi.getEgoCategorizationCreateApprovalStatistics(categorizationReviewStore.bagId)
            .then(r => setApprovalStats(r.data))
            .finally(() => setIsLoading(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categorizationReviewStore.bagId, isLoading, approvalStats]);

    return (
        <EditorModalComponent
            titleId={"review-modal-title"}
            className="review-model-component confirm-send-for-approval-component"
            title={
                (approvalController.isApprovalJobRunning === undefined) ?
                    "Checking for previous approval being processed" : (
                        approvalController.isApprovalJobRunning ?
                            "Approval in Progress" :
                            "Sending your review work for approval")
            }
            titleIcon={approvalController.isApprovalJobRunning ? <AccessTimeIcon/> : undefined}
            saveLabel={
                (approvalController.isApprovalJobRunning === undefined) ?
                    "Checking" : (
                        approvalController.isApprovalJobRunning ?
                            "Please wait" :
                            "Send for approval")
            }
            canSave={!approvalController.isApprovalJobRunning && !approvalController.isBusySendForApproval && approvalStats?.rows !== 0}
            saveTooltip={approvalStats?.rows === 0 ? 'Nothing to send for approval ...' : ''}
            onSave={() => {
                approvalController.sendForApproval(
                    () => {
                        // Reload the page when the approval is send
                        categorizationReviewStore.reviewPageController.loadData(true)
                    },
                    (err) => {
                        rollbar.error(err)
                    },
                )
            }}
            isSaving={approvalController.isApprovalJobRunning || approvalController.isBusySendForApproval}
            onClose={() => approvalController.setSendForApprovalDialog(false)}
            isError={approvalController.isSendForApprovalError}
            largerErrorMessages
        >
            <Grid container justifyContent="center" alignContent="center" sx={{minHeight: '5em'}}>
                {!approvalStats
                    ? <Grid><CircularProgress size={14}/></Grid>
                    : <Grid size={8}>
                        <Grid container spacing={2} justifyContent="space-evenly">
                            <Grid size={4}>
                                <Grid container direction="column" alignItems="center">
                                    <Grid>
                                        <Typography variant="subtitle2" component="span">
                                            Spend reviewed
                                        </Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography variant="h6" component="span">
                                            <CurrencyComponent v={approvalStats.spend}/>
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid size={4}>
                                <Grid container direction="column" alignItems="center">
                                    <Grid>
                                        <Typography variant="subtitle2" component="span">
                                            Suppliers reviewed
                                        </Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography variant="h6" component="span">
                                            {approvalStats?.suppliers}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid size={4}>
                                <Grid container direction="column" alignItems="center">
                                    <Grid>
                                        <Typography variant="subtitle2" component="span">
                                            Rows reviewed
                                        </Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography variant="h6" component="span">
                                            {approvalStats.rows}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                }
            </Grid>

            {approvalController.isApprovalJobRunning && <Typography variant="body2" sx={{mt: 2}}>
                <Alert severity="warning">
                    {/*TODO: When CAT-2547 is implemented, we don't need this warning anymore*/}
                    Currently another approval is being processed, please wait until it completes to submit a new one.
                </Alert>
            </Typography>}
            {!approvalController.isApprovalJobRunning && <>
                <Typography variant="body2" sx={{mt: 2}}>
                    <Alert severity="info">
                        Once you submit your review for approval, you won’t be able to make changes for a few minutes
                        while the request is processed.
                        After approval is created, you can continue reviewing.
                    </Alert>
                </Typography>
                <TextField
                    sx={{mt: 1}}
                    multiline
                    fullWidth
                    id="review-editor-modal-approval-note-field"
                    label="Notes for the approver (optional)"
                    // error={false}
                    // helperText={}
                    variant="outlined"
                    value={approvalController.approvalNotes}
                    onChange={e => approvalController.setApprovalNotes(e.target.value)}
                    disabled={approvalController.isBusySendForApproval}
                />
            </>}
        </EditorModalComponent>
    );
})
